import React, { useEffect, useRef, useState } from "react";
import {
  Pagination,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import styled from "styled-components";
import CircleIcon from "@mui/icons-material/Circle";
import { IconCaretUpDownFilled } from "@tabler/icons-react";
import colors from "../../assest/theme/base/colors";
// import AccessComponent from "./AccessComponent";
// import { getFormattedStatus } from "./getFormattedStatus";

const CustomPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    color: "#000000",
    borderColor: "#fff",
    backgroundColor: "#fff",
    height: "25px", // Keeping consistent with Material-UI size
    minWidth: "25px", // Overrides default minWidth
    fontSize: "0.75rem", // Adjust font size
  },
  "& .MuiPagination-ul": {
    padding: "0", // Remove extra padding around pagination items
    margin: "0", // Remove extra margin
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    background: colors.buttonColor,
    color: "#FFFFFF",
  },
  "& .MuiPaginationItem-root:hover": {
    background: colors.buttonColor,
    color: "#FFFFFF",
  },
}));

const getButtonStyle = (status) => {
  switch (status) {
    case "Active":
      return { ...styles.button, ...styles.activeButton };

    case "Inactive":
      return { ...styles.button, ...styles.inactiveButton };

    case "SELECTED":
    case "PROJECT_SUCCESS":
      return { ...styles.button, ...styles.selectButton };

    case "REJECTED":
    case "LEFT":
      return { ...styles.button, ...styles.rejectButton };

    case "Screening":
    case "HOLD":
      return { ...styles.button, ...styles.screeningButton };

    case "INTERVIEWED":
      return { ...styles.button, ...styles.InterviewedButton };

    case "ONBOARDED":
    case "WORKING":
      return { ...styles.button, ...styles.OnboardedButton };

    case "SHORTLISTED":
      return { ...styles.button, ...styles.ScreenedButton };

    case "NOT_SHORTLISTED":
    case "NOT_JOINED":
      return { ...styles.button, ...styles.notScreenedButton };

    default:
      return styles.button;
  }
};

const getButtonIcon = (status) => {
  switch (status) {
    case "Active":
      return <CheckCircleIcon style={{ marginRight: "2px" }} />;
    case "Inactive":
      return <HighlightOffIcon style={{ marginRight: "2px" }} />;
    case "Screening":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "INTERVIEWED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "ONBOARDED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "SHORTLISTED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "NOT_SHORTLISTED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "SELECTED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "REJECTED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "HOLD":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "WORKING":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "LEFT":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "NOT_JOINED":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    case "PROJECT_SUCCESS":
      return <CircleIcon style={{ marginRight: "2px" }} />;
    default:
      return null;
  }
};

const styles = {
  container: {
    padding: "20px",
    backgroundColor: "transparent",
  },
  tableContainer: {
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: "#f9f9f9",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    tableLayout: "fixed",
  },
  tableHeader: {
    backgroundColor: "#F4F5F7",
  },
  headerRow: {
    backgroundColor: "#F4F5F7",
    borderRadius: "8px 8px 0 0",
  },
  headerText: {
    fontWeight: "600",
    color: "#110072",
    fontSize: "15px",
    padding: "12px 12px 12px 30px",
    textAlign: "left",
    borderBottom: "2px solid #E5E7EB",
  },
  cell: {
    padding: "8px 8px 8px 30px",
    fontSize: "14px",
    color: "#4B5563",
    textAlign: "left",
    borderBottom: "1px solid #E5E7EB",
  },
  button: {
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "capitalize",
    padding: "4px 8px",
    borderRadius: "6px",
  },
  activeButton: {
    backgroundColor: "rgba(34, 197, 94, 0.1)",
    color: "#22C55E",
    width: "100px",
  },
  inactiveButton: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    color: "#EF4444",
    width: "100px",
  },
  screeningButton: {
    color: "#FAA215",
  },
  InterviewedButton: {
    color: "#008CFF",
  },
  OnboardedButton: {
    color: "#2BA300",
  },
  ScreenedButton: {
    color: "#FFBF00",
  },
  notScreenedButton: {
    color: "#FF0001",
  },
  selectButton: {
    color: "#22C55E",
  },
  rejectButton: {
    color: "#EF4444",
  },
  actions: {
    display: "flex",
  },
  pagination: {
    marginTop: "16px",
    display: "flex",
    justifyContent: "flex-end",
  },
  row: {
    transition: "background-color 0.2s ease-in-out", // Smooth transition
  },
  rowHover: {
    backgroundColor: "#f3f4f6", // Light gray background on hover
    cursor: "pointer", // Pointer cursor on hover
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
  },
};

const DynamicTable = ({
  columns = [],
  rows = [],
  onView,
  onEdit,
  onDelete,
  name,
  isLoading, // New prop to indicate isLoading state
  edit = true,
  deletes = true,
  view = true,
  handleview,
  ActivityLog,
}) => {
  const rowsPerPage = 10;
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [sortedRows, setSortedRows] = useState([]);

  // Drag state & ref
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Mouse/touch drag handlers
  const startDrag = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const x = e.pageX || e.touches?.[0]?.pageX;
    setStartX(x);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const onDrag = (e) => {
    if (!isDragging) return;
    const x = e.pageX || e.touches?.[0]?.pageX;
    const walk = x - startX;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const endDrag = () => {
    setIsDragging(false);
  };

  // Add mouse and touch event listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("mousedown", startDrag);
    container.addEventListener("mousemove", onDrag);
    container.addEventListener("mouseup", endDrag);
    container.addEventListener("mouseleave", endDrag);

    container.addEventListener("touchstart", startDrag);
    container.addEventListener("touchmove", onDrag);
    container.addEventListener("touchend", endDrag);

    return () => {
      container.removeEventListener("mousedown", startDrag);
      container.removeEventListener("mousemove", onDrag);
      container.removeEventListener("mouseup", endDrag);
      container.removeEventListener("mouseleave", endDrag);

      container.removeEventListener("touchstart", startDrag);
      container.removeEventListener("touchmove", onDrag);
      container.removeEventListener("touchend", endDrag);
    };
  }, [isDragging, startX, scrollLeft]);

  useEffect(() => {
    setSortedRows([...rows]); // Update sortedRows when rows prop changes
  }, [rows]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedRows = sortedRows.slice(startIndex, endIndex);

  const handleSort = (key) => {
    const newDirection =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";

    const sortedData = [...sortedRows].sort((a, b) => {
      if (a[key] < b[key]) {
        return newDirection === "asc" ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return newDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    setSortedRows(sortedData);
    setSortConfig({ key, direction: newDirection });
  };

  return (
    <Box style={styles.container}>
      <Box style={styles.tableContainer}>
        <Box
          ref={scrollContainerRef}
          sx={{
            overflowX: "auto",
            width: "100%",
          }}
        >
          <table style={styles.table}>
            <thead style={{ backgroundColor: "#E6E6E6" }}>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    style={{
                      ...styles.headerText,
                      width: column?.width || "auto",
                    }}
                    onClick={() => handleSort(column.field)}
                  >
                    {column.label}
                    <IconCaretUpDownFilled
                      size={15}
                      style={{ marginBottom: "-2px" }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      fontStyle: "italic",
                      color: "#555",
                    }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : displayedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      fontStyle: "italic",
                      color: "#555",
                    }}
                  >
                    No data available.
                  </td>
                </tr>
              ) : (
                displayedRows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        styles.rowHover.backgroundColor)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                    onClick={() => ActivityLog && handleview(row)}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        style={{
                          ...styles.cell,
                          width: column?.width || "auto",
                          textAlign: "left",
                        }}
                      >
                        {column.field === "status" ? (
                          <Button
                            style={getButtonStyle(row[column.field])}
                            startIcon={getButtonIcon(row[column.field])}
                          >
                            {/* {getFormattedStatus(row[column.field])} */}
                          </Button>
                        ) : column.field === "actions" ? (
                          <Box style={styles.actions}>
                            {view && (
                              // <AccessComponent name={name} action="View">
                              <IconButton
                                onClick={() => {
                                  onView(row.id);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              // {/* </AccessComponent> */}
                            )}
                            {edit && (
                              // <AccessComponent name={name} action="Edit">
                              <IconButton
                                onClick={() => {
                                  onEdit(row.id);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              // </AccessComponent>
                            )}
                            {deletes && (
                              // <AccessComponent name={name} action="Delete">
                              <IconButton
                                onClick={() => {
                                  onDelete(row.id);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              // </AccessComponent>
                            )}
                          </Box>
                        ) : column.field === "permissions" ? (
                          <Tooltip
                            title={row[column.field] || "No permissions"}
                            placement="right"
                            sx={{
                              "& .MuiTooltip-tooltip": {
                                maxHeight: "100px", // Set maximum height
                                overflowY: "auto", // Enable scrolling for overflowing content
                              },
                            }}
                          >
                            <span>See all permissions</span>
                          </Tooltip>
                        ) : (
                          row[column.field]
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      </Box>
      <Box style={styles.pagination}>
        <CustomPagination
          count={Math.ceil(sortedRows.length / rowsPerPage)}
          page={page}
          onChange={handleChangePage}
          variant="outlined"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default DynamicTable;
